import { Link } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/auth-store';

interface HeaderProps {
  showAuth?: boolean;
  showNav?: boolean;
  onMenuClick?: () => void;
  isMenuOpen?: boolean;
}

export function Header({
  showAuth = true,
  showNav = false,
  onMenuClick,
  isMenuOpen = false,
}: HeaderProps) {
  const { isAuthenticated, user, logout } = useAuthStore();

  return (
    <header className="bg-white border-b border-lilac-900 sticky top-0 z-40" role="banner">
      <div className="w-full px-4 sm:px-8 mx-auto">
        <div className="flex justify-between items-center h-16 min-h-[64px]">
          {/* Logo */}
          <Link
            to={isAuthenticated() ? '/dashboard' : '/'}
            className="flex items-center"
            aria-label="CareCal - Go to home page"
          >
            <img src="/logo.png" alt="CareCal" className="h-8 w-auto" />
          </Link>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            {showAuth && (
              <>
                {isAuthenticated() ? (
                  <div className="flex items-center space-x-4">
                    <span
                      className="hidden sm:inline font-body text-night-700"
                      aria-label={`Logged in as ${user?.firstName || user?.email}`}
                    >
                      {user?.firstName || user?.email}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={logout}
                      className="hidden sm:inline-flex items-center gap-2"
                      aria-label="Log out of your account"
                    >
                      <LogOut className="h-4 w-4" aria-hidden="true" />
                      Log Out
                    </Button>
                  </div>
                ) : (
                  <div
                    className="hidden sm:flex items-center space-x-2"
                    role="navigation"
                    aria-label="Authentication"
                  >
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/login" aria-label="Log in to your account">
                        Log in
                      </Link>
                    </Button>
                    <Button size="sm" asChild>
                      <Link to="/register" aria-label="Create a new account">
                        Sign Up
                      </Link>
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Mobile menu button */}
            {showNav && isAuthenticated() && (
              <button
                onClick={onMenuClick}
                className="md:hidden p-2 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-navigation"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6 text-blueviolet" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6 text-blueviolet" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
